import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CustomersDrilldownKind,
  CustomersDrilldownRow,
  CustomersReportQuery,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { ExportCsvButton } from './ExportCsvButton';
import { useCustomersRecordsQuery } from '../api/customersOptical.api';
import { MoneyByCurrencyText } from './MoneyByCurrencyText';
import { RecordPager, SimpleTable, CountText } from './ReportBlocks';

const PAGE_SIZE = 20;

export interface CustomersRecordsRequest {
  kind: CustomersDrilldownKind;
  customerId?: string;
  /** What the figure is called, already translated. */
  title: string;
}

/**
 * The records behind one Customers figure, paged and totalled by the server under the report's own
 * scope, so the total shown is the figure itself. Customer numbers and names only — no phone, email
 * or date of birth is ever asked for.
 */
export function CustomersRecords({
  query,
  request,
  onClose,
}: {
  query: CustomersReportQuery;
  request: CustomersRecordsRequest;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const count = (value: number) => <CountText value={value} />;
  const [page, setPage] = useState(1);
  const recordsQuery = {
    ...query,
    kind: request.kind,
    customerId: request.customerId,
    page,
    pageSize: PAGE_SIZE,
  };
  const records = useCustomersRecordsQuery(recordsQuery);
  const pages = records.data ? Math.max(1, Math.ceil(records.data.total / PAGE_SIZE)) : 1;
  const headingId = `customers-records-${request.kind}-${request.customerId ?? 'all'}`;
  const ltr = (value: ReactNode) => <span dir="ltr">{value ?? '—'}</span>;
  const money = (row: CustomersDrilldownRow, minor: number | null) =>
    minor === null || row.currencyCode === null
      ? '—'
      : ltr(formatMoney(minor, locale, row.currencyCode));
  const who = (row: CustomersDrilldownRow) => (
    <span>
      {row.name ?? t('reports.customers.unknownCustomer')}{' '}
      {row.customerNumber ? (
        <span className="text-muted text-xs" dir="ltr">
          {row.customerNumber}
        </span>
      ) : null}
    </span>
  );
  const col = (key: string) => t(`reports.customers.columns.${key}`);

  const table = (rows: CustomersDrilldownRow[]) => {
    switch (request.kind) {
      case 'REGISTRATIONS':
        return (
          <SimpleTable
            headers={[col('customer'), col('registeredOn'), col('registeredAt')]}
            rows={rows.map((row) => ({
              key: row.id,
              cells: [
                who(row),
                ltr(row.businessDate),
                row.branchName ?? t('reports.customers.noRegistrationBranch'),
              ],
            }))}
          />
        );
      case 'CUSTOMER_ACTIVITY':
        return (
          <SimpleTable
            headers={[col('customer'), col('orders'), col('netSales')]}
            ends={[1, 2]}
            rows={rows.map((row) => ({
              key: row.id,
              cells: [
                who(row),
                count(row.confirmedOrders ?? 0),
                <MoneyByCurrencyText key="net" amounts={row.amount ?? []} />,
              ],
            }))}
          />
        );
      case 'OWING_CUSTOMERS':
        return (
          <SimpleTable
            headers={[col('customer'), col('owingOrders'), col('outstanding')]}
            ends={[1, 2]}
            rows={rows.map((row) => ({
              key: row.id,
              cells: [
                who(row),
                count(row.owingOrders ?? 0),
                <MoneyByCurrencyText key="owed" amounts={row.amount ?? []} />,
              ],
            }))}
          />
        );
      case 'CUSTOMER_SALES':
        return (
          <SimpleTable
            headers={[
              col('event'),
              col('reference'),
              col('date'),
              col('salesBranch'),
              col('amount'),
            ]}
            ends={[4]}
            rows={rows.map((row) => ({
              key: row.id,
              cells: [
                row.event ? t(`reports.sales.records.events.${row.event}`) : '—',
                ltr(row.reference),
                ltr(row.businessDate),
                row.branchName ?? '—',
                money(row, row.amountMinor),
              ],
            }))}
          />
        );
      default:
        return (
          <SimpleTable
            headers={[
              col('reference'),
              col('date'),
              col('salesBranch'),
              col('total'),
              col('settled'),
              col('outstanding'),
            ]}
            ends={[3, 4, 5]}
            rows={rows.map((row) => ({
              key: row.id,
              cells: [
                ltr(row.reference),
                ltr(row.businessDate),
                row.branchName ?? '—',
                money(row, row.totalMinor),
                money(row, row.settledMinor),
                money(row, row.amountMinor),
              ],
            }))}
          />
        );
    }
  };

  return (
    <Card className="min-w-0 p-4">
      <section aria-labelledby={headingId}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 id={headingId} className="text-ink font-semibold">
            {request.title}
          </h3>
          <span className="flex flex-wrap items-baseline gap-3">
            <ExportCsvButton
              target={{
                method: 'GET',
                path: '/reports/customers/records/export',
                query: recordsQuery,
              }}
            />
            <button
              type="button"
              onClick={onClose}
              className="text-brand-700 text-sm font-medium hover:underline"
            >
              {t('reports.sales.records.close')}
            </button>
          </span>
        </div>
        {records.isError ? (
          <div className="mt-3">
            <Alert tone="error">{t('reports.sales.records.loadError')}</Alert>
          </div>
        ) : null}
        {records.isPending ? (
          <div
            className="bg-surface border-line mt-3 h-24 animate-pulse rounded-lg border"
            aria-hidden
          />
        ) : null}
        {records.data ? (
          <div className="text-muted mt-2 flex flex-wrap items-baseline gap-x-2 text-sm">
            {request.kind === 'REGISTRATIONS' ? (
              <span>{t('reports.customers.records.customers', { count: records.data.total })}</span>
            ) : (
              <>
                <span>{t('reports.finance.records.totalOf', { count: records.data.total })}</span>
                <MoneyByCurrencyText
                  amounts={records.data.totals}
                  className="text-ink font-medium"
                />
              </>
            )}
          </div>
        ) : null}
        {records.data && records.data.rows.length === 0 ? (
          <p className="text-muted mt-3 text-sm">{t('reports.sales.records.empty')}</p>
        ) : null}
        {records.data && records.data.rows.length > 0 ? table(records.data.rows) : null}
        {records.data ? <RecordPager page={page} pages={pages} onPage={setPage} /> : null}
      </section>
    </Card>
  );
}
